/**
 * @fileoverview Filters body for the Sales page to be injected into FlyingMenu.
 * @module shared/components/filters/extensions/SalesFiltersExtension
 */

import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFilter } from '@/shared/hooks/useFilter'
import { MultiCombobox, type ComboboxOption } from '@/shared/components/filters/MultiCombobox'

/**
 * Renders DateRange + Countries + SKUs controls.
 * The surrounding card and header are provided by FlyingMenu.
 *
 * @component
 */
export function SalesFiltersExtension(): ReactElement {
  const { t } = useTranslation('common')
  const {
    countries,
    setCountries,
    availableCountries,
    isLoadingCountries,
    skus,
    setSkus,
    availableSkus,
    isLoadingSkus,
  } = useFilter()

  const countryOptions: ComboboxOption[] = useMemo(
    () =>
      (availableCountries ?? []).map(c => ({
        value: c.code,
        text: c.name,
      })),
    [availableCountries]
  )

  const skuArray = useMemo(
    () => (Array.isArray(availableSkus) ? availableSkus : []),
    [availableSkus]
  )
  const skuOptions: ComboboxOption[] = useMemo(
    () => skuArray.map(s => ({ value: s.name, text: s.name })),
    [skuArray]
  )

  return (
    <>
      <MultiCombobox
        options={countryOptions}
        selectedValues={countries}
        onChange={setCountries}
        placeholder={t('filters.searchCountries')}
        disabled={isLoadingCountries}
        isLoading={isLoadingCountries}
        className="shrink-0 basis-40 sm:basis-44 md:basis-48"
      />

      <MultiCombobox
        options={skuOptions}
        selectedValues={skus}
        onChange={setSkus}
        placeholder={t('filters.searchSkus')}
        disabled={isLoadingSkus}
        isLoading={isLoadingSkus}
        className="shrink-0 basis-36 sm:basis-36 md:basis-36"
      />
    </>
  )
}
